'use client';

import {
  Flex,
  Column,
  Input,
  Button,
  ButtonGroup,
  Dialog,
  Text,
  Separator,
} from '@/_components';
import { LayoutContext } from '@/_contexts/LayoutProvider';
import { OverviewContext } from '@/_contexts/admin/OverviewProvider';
import { OverviewFilters } from '../OverviewFilters';
import { OverviewHeaderProps } from './OverviewHeader.types';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { useDebounce } from '@/_hooks/useDebounce';
import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
  type FC,
} from 'react';
import type { OverviewFilterObject } from '@/_types/overview';
import { ScheduledQuestionnaireDialog } from './components/ScheduledQuestionnaireDialog';
import { DEFAULT_DEBOUNCE_TIME } from '@/_constants/debounce';
import { clearQueuedQuestionnaireSends } from '@/_api/admin-firms';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const OverviewHeader: FC<OverviewHeaderProps> = () => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const [search, setSearch] = useState<string>('');
  const [showScheduledDialog, setShowScheduledDialog] =
    useState<boolean>(false);
  const [showCancelConfirmDialog, setShowCancelConfirmDialog] =
    useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const { permissions, token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const {
    loadQuestionnaires,
    isLoading,
    isSending,
    setSending,
    setSendingTemplate,
  } = useContext(QuestionnaireContext);
  const { setFilterContent, isAssistantOpen } = useContext(LayoutContext);
  const { resetFilters, isFiltering, filter, setFilter, setPagination } =
    useContext(OverviewContext);

  const onSearchChange = (event: any) => setSearch(event.target.value);
  const searchDebounce = useDebounce(search, DEFAULT_DEBOUNCE_TIME);

  const onResetState = () => setSearch('');

  const onEditFilters = () => setFilterContent(<OverviewFilters />);

  const onRefresh = () => loadQuestionnaires();

  const onToggleScheduled = () => setShowScheduledDialog(!showScheduledDialog);

  const onOpenCancelConfirmDialog = () => setShowCancelConfirmDialog(true);
  const onCloseCancelConfirmDialog = () => setShowCancelConfirmDialog(false);

  const onConfirmCancelAll = useCallback(async () => {
    if (!token) return;

    setIsCancelling(true);

    try {
      const result = await clearQueuedQuestionnaireSends({ token });

      if (result && 'removed_count' in result) {
        addToast(
          'success',
          `Successfully cancelled ${result.removed_count} queued questionnaire ${result.removed_count === 1 ? 'send' : 'sends'}.`,
        );
      }

      onCloseCancelConfirmDialog();
      loadQuestionnaires();
    } catch (error) {
      console.error('Failed to cancel queued questionnaire sends:', error);
      addToast('error', 'Failed to cancel queued questionnaire sends', {
        error,
      });
    } finally {
      setIsCancelling(false);
    }
  }, [token, loadQuestionnaires, addToast]);

  const updateSearch = useCallback(
    (searchTerm: string) => {
      const newFilters: OverviewFilterObject = {
        ...filter,
        search_term: searchTerm,
        page: 0,
      };

      setPagination((previous) => ({ ...previous, pageIndex: 0 }));
      setFilter(newFilters);
    },
    [filter, setFilter, setPagination],
  );

  const onResetFilters = useCallback(() => {
    setSearch('');
    resetFilters();
    setFilterContent(null);
  }, [resetFilters, setFilterContent]);

  const onToggleSending = useCallback(() => {
    setSending(!isSending);
  }, [setSending, isSending]);

  const onSchedule = useCallback(() => {
    setSendingTemplate(true);
    setShowScheduledDialog(false);
  }, [setSendingTemplate, setShowScheduledDialog]);

  const onKeyPress = useCallback(
    (event: any) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        updateSearch(event.target.value);
      }
    },
    [updateSearch],
  );

  const hasSendQuestionnairePermission: boolean = getHasPermission(
    PermissionsMapEnum['questionnaire.add'],
    permissions,
  );

  const hasFirmSettingsPermissions: boolean = getHasPermission(
    PermissionsMapEnum['firm.settings'],
    permissions,
  );

  useEffect(() => {
    if (!isLoading && search) {
      searchInputRef?.current?.focus?.();
    }
  }, [isLoading, search, searchInputRef]);

  useEffect(() => {
    const _loadSearch = () => {
      const oldSearch: string = filter?.search_term?.trim() || '';

      if (oldSearch !== search?.trim()) {
        updateSearch(search);
      }
    };

    _loadSearch();
  }, [searchDebounce]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Dialog
        title="Cancel All Pending Questionnaire Sends?"
        isOpen={showCancelConfirmDialog}
        onClose={onCloseCancelConfirmDialog}
        isNarrow={true}
      >
        <Dialog.Content>
          <Text type="paragraph">
            Are you sure you want to{' '}
            <b>cancel all pending questionnaire sends</b>? This will cancel any
            questionnaires that have been queued for sending, but have not yet
            been sent. This action cannot be undone.
          </Text>
        </Dialog.Content>
        <Dialog.Footer>
          <ButtonGroup align="space-between">
            <Button
              value="Cancel"
              onClick={onCloseCancelConfirmDialog}
              intent="secondary"
              isDisabled={isCancelling}
            />
            <Button
              value="Yes, cancel all"
              onClick={onConfirmCancelAll}
              intent="danger"
              isLoading={isCancelling}
              isDisabled={isCancelling}
            />
          </ButtonGroup>
        </Dialog.Footer>
      </Dialog>
      <ScheduledQuestionnaireDialog
        isOpen={showScheduledDialog}
        onClose={onToggleScheduled}
        onSchedule={onSchedule}
      />
      <Flex isBetween={true} isYCentered={true} gap={6}>
        <Column width={isAssistantOpen ? 200 : 360}>
          <Input
            ref={searchInputRef}
            label="Search"
            iconRight={!!search ? 'x' : undefined}
            onRightClick={!!search ? onResetState : undefined}
            isLabelHidden={true}
            isLoading={isLoading && !!search}
            placeholder="Search by questionnaire name, entity, or user"
            value={search}
            onChange={onSearchChange}
            onKeyPress={onKeyPress}
            isDisabled={isLoading}
          />
        </Column>

        <Flex isYCentered={true} gap={6}>
          <ButtonGroup isSameLine={true}>
            <Button
              value="Refresh"
              withTooltip={true}
              isIconOnly={true}
              iconLeft="refresh"
              isDisabled={isLoading}
              intent="ghost"
              size="small"
              onClick={onRefresh}
            />
            {!isAssistantOpen && hasFirmSettingsPermissions && (
              <Button
                onClick={onOpenCancelConfirmDialog}
                isIconOnly={true}
                withTooltip={true}
                value="Cancel all pending questionnaire sends"
                iconLeft="stop-circle"
                size="small"
                intent="secondary"
                isDisabled={isLoading || !hasSendQuestionnairePermission}
              />
            )}
            {isFiltering && (
              <Button
                value="Reset Filters"
                iconLeft="x"
                isDisabled={isLoading}
                intent="secondary"
                size="small"
                onClick={onResetFilters}
              />
            )}
            <Button
              value="Filter"
              iconLeft="funnel"
              isDisabled={isLoading}
              intent="secondary"
              size="small"
              onClick={onEditFilters}
            />
            {!isAssistantOpen && (
              <>
                <Separator isVertical height={32} />
                <Button
                  value="Scheduled questionnaires"
                  iconLeft="calendar"
                  size="small"
                  intent="secondary"
                  isIconOnly={true}
                  withTooltip={true}
                  onClick={onToggleScheduled}
                  isDisabled={isLoading || !hasSendQuestionnairePermission}
                />
              </>
            )}
            <Button
              value="Send Questionnaire"
              size="small"
              isDisabled={!hasSendQuestionnairePermission}
              intent="primary"
              onClick={onToggleSending}
            />
          </ButtonGroup>
        </Flex>
      </Flex>
    </>
  );
};
